import type { ProcessButtonState } from "@msuite/utils/types";
import { useEffect, useState } from "react";
import { Icon } from "../icons";
import { Spinner } from "../spinner";
import { cn } from "../utils";

/** Props Interface */
interface UseProcessButtonProps {
	onBeforeClick?: () => Promise<boolean>;
	onClick: () => Promise<ProcessButtonState>;
	onAfterClick?: (state: ProcessButtonState) => Promise<void>;
	initialState?: ProcessButtonState;
	labels: Record<ProcessButtonState, string>;
	canRestart?: boolean;
	isDisabled?: boolean;
	isPreparing?: boolean;
}

const ICON_SIZE = 14;

export function useProcessButton({
	onBeforeClick,
	onClick,
	onAfterClick,
	initialState = "pending",
	labels,
	canRestart = true,
	isDisabled = false,
	isPreparing = false,
}: UseProcessButtonProps) {
	/** State */
	const [state, setState] = useState<ProcessButtonState>(initialState);

	/** Functions */
	async function handleOnClick() {
		const previousState = state;
		try {
			setState("processing");
			if (onBeforeClick) {
				const shouldContinue = await onBeforeClick();
				if (!shouldContinue) {
					throw new Error("User cancelled");
				}
			}
			if (!canRestart) {
				if (state === "completed") {
					throw new Error("Button cannot be restarted");
				}
			}
			const newState = await onClick();
			setState(newState);
			if (onAfterClick) {
				await onAfterClick(newState);
			}
		} catch {
			setState(previousState);
		}
	}

	function getLabel() {
		return labels[state];
	}

	function getIsDisabled() {
		if (isDisabled) return true;
		if (state === "processing") return true;
		if (!canRestart && state === "completed") return true;
		return false;
	}

	function getIcon(state: ProcessButtonState): () => React.ReactNode {
		if (isDisabled)
			return () => (
				<Icon
					name="Lock"
					size={ICON_SIZE}
					className="stroke-1.5 stroke-primary-900 fill-primary-900/5"
				/>
			);
		switch (state) {
			case "pending":
				return () => (
					<Icon
						name="Send"
						className={cn(
							"stroke-1.5 stroke-primary-700",
							"translate-x-[-0.5px] translate-y-[0.5px]",
						)}
						size={ICON_SIZE}
					/>
				);
			case "processing":
				return () => <Spinner />;
			case "completed":
				return () => (
					<Icon
						name="Check"
						className="stroke-1.5 stroke-emerald-500"
						size={ICON_SIZE}
					/>
				);
			case "failed":
				return () => (
					<Icon
						name="X"
						className="stroke-3 stroke-rose-500"
						size={ICON_SIZE}
					/>
				);
		}
	}

	/** Effects */
	useEffect(() => {
		setState(initialState);
	}, [isPreparing, initialState]);

	/** Return */
	return { state, handleOnClick, getLabel, getIsDisabled, getIcon };
}

/** Return Type */
export type ProcessButtonController = ReturnType<typeof useProcessButton>;
